import { Breadcrumbs, MainContentLayout, showToast, ToastTypeEnum } from '@wonderschool/common-base-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectFirstLocation } from '../../Components/DateTime/DateTime';
import PageHeader from '../../Components/Shared/PageHeader';
import { useOrganization } from '../../hooks/useOrganizations';
import useRooms from '../../hooks/useRooms';
import { useUser } from '../../hooks/useUser';
import { paths } from '../../navigation/paths';
import { logError } from '../../rollbar';
import { useAllStudents } from '../../students/studentsHooks';
import { StepBilling } from '../AddFamilies/StepBilling';
import { hasBillingAmount, RosterRow, toBillableRows } from '../AddFamilies/types';
import { WizardSteps } from '../AddFamilies/WizardSteps';
import { confirmAddToBilling } from './addToBillingAPI';
import { studentToExistingStudent, toRosterRow } from './existingStudents';
import { ReviewBilling } from './ReviewBilling';
import { SelectExistingStudents } from './SelectExistingStudents';

const STEP_LABELS = ['Select students', 'Set up billing', 'Review'];

/**
 * Add-to-billing flow (Job B): put families who are ALREADY in CCMS onto a
 * recurring plan. Lives in Invoices & Payments (Recurring Plans), not the Add
 * Families wizard, because there's nothing to add, only to bill. Reuses the
 * shared billing step and stepper from the Add Families flow.
 *
 * Students are read live from the org; confirm creates a recurring invoice plan
 * per billed family via `createOrganizationInvoicePlan`. Creating the plan doc
 * needs nothing from Stripe. The "charge on the platform balance, hold payout
 * until Stripe onboarding completes" behavior is delivered separately, behind
 * the `delayed-payout-setup` flag (PR #5419 + its backend), which this flow
 * assumes is enabled before any invoice is actually charged.
 *
 * @handoff `alreadyBilled` is proxied by `enrollment.billingPlan`; cross-check
 * active recurring plans for exact accuracy.
 */
export function AddToBillingFlow({ chrome = true }: { chrome?: boolean } = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Read the org's REAL students so the flow reflects what's actually in CCMS
  // (no students -> import first; real families -> show them). No more mock list.
  const organization = useOrganization() as any;
  const user = useUser() as any;
  const firstLocation = useSelector(selectFirstLocation) as any;
  const timezone = firstLocation?.timezone ?? 'utc';
  const rooms = useRooms();
  const activeRooms = useMemo(
    () => (rooms || []).filter((r: any) => !r?.isArchived).map((r: any) => ({ id: r.id, name: r.name })),
    [rooms]
  );
  const studentsSlice = useAllStudents() as any;
  const realStudents = useMemo(() => {
    const roomName = (id?: string) => rooms.find((r: any) => r.id === id)?.name || '';
    return (studentsSlice?.list ?? []).map((s: any) => studentToExistingStudent(s, roomName));
  }, [studentsSlice, rooms]);

  const students = realStudents;

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [roster, setRoster] = useState<RosterRow[]>([]);
  const [frequency, setFrequency] = useState<RosterRow['billingFrequency']>('monthly');
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    document.title = 'Add families to billing | Wonderschool';
  }, []);

  // Once students load, default everyone not-already-billed to selected — the goal
  // is fast onboarding, so deselecting exceptions beats ticking each one.
  const initializedSelection = useRef(false);
  useEffect(() => {
    if (!initializedSelection.current && students.length > 0) {
      setSelectedIds(students.filter((s) => !s.alreadyBilled).map((s) => s.id));
      initializedSelection.current = true;
    }
  }, [students]);

  function toggle(id: string) {
    setSelectedIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  function selectAll(select: boolean) {
    setSelectedIds(select ? students.filter((s) => !s.alreadyBilled).map((s) => s.id) : []);
  }

  // Build the billing roster from the picked students when leaving step 1.
  function goToBilling() {
    const picked = students.filter((s) => selectedIds.includes(s.id));
    setRoster((prev) => {
      // Preserve any tuition already typed if the user steps back and forth.
      const byId = new Map(prev.map((r) => [r.id, r]));
      return picked.map((s) => byId.get(s.id) ?? toRosterRow(s));
    });
    setStep(2);
  }

  const billedCount = useMemo(() => roster.filter((r) => hasBillingAmount(r)).length, [roster]);

  async function onConfirm() {
    if (!organization?.id) return;
    setIsSaving(true);
    try {
      const studentsById = new Map<string, any>((studentsSlice?.list ?? []).map((s: any) => [s.id, s]));
      const created = await confirmAddToBilling({
        organizationId: organization.id,
        // Same mapping the Add Families wizard uses: turn the recurring due day
        // collected by the shared `StepBilling` into the first actual due date.
        // Passing `roster` raw billed every family on `blankRow()`'s today
        // default, with no way for the provider to change it.
        rows: toBillableRows(roster),
        studentsById,
        createdBy: { id: user?.uid, displayName: user?.displayName },
        timezone,
      });
      showToast(
        ToastTypeEnum.Success,
        t('{{count}} {{noun}} put on billing', { count: created, noun: created === 1 ? t('student') : t('students') })
      );
      navigate(paths.recurringPlans);
    } catch (err) {
      logError('AddToBilling confirm failed', err);
      showToast(ToastTypeEnum.Error, t('Something went wrong creating the plans. Please try again.'));
    } finally {
      setIsSaving(false);
    }
  }

  const content = (
    <div className="max-w-6xl space-y-4">
      <Breadcrumbs
        items={[
          { label: t('Recurring plans'), callback: () => navigate(paths.recurringPlans) },
          { label: t('Add families to billing'), isCurrentLevel: true },
        ]}
      />

      {/* Keyed by step so each screen fades/rises in instead of hard-swapping. */}
      <div key={step} className="motion-safe:animate-stepIn">
        {step === 1 && (
          <SelectExistingStudents
            stepper={<WizardSteps current={0} steps={STEP_LABELS} />}
            students={students}
            selectedIds={selectedIds}
            onToggle={toggle}
            onSelectAll={selectAll}
            onImport={() => navigate(`${paths.addFamilies}?start=upload`)}
            onBack={() => navigate(paths.recurringPlans)}
            onContinue={goToBilling}
          />
        )}

        {step === 2 && (
          <StepBilling
            stepper={<WizardSteps current={1} steps={STEP_LABELS} />}
            roster={roster}
            frequency={frequency}
            rooms={activeRooms}
            requireAmount
            onChange={setRoster}
            onFrequencyChange={(freq) => {
              setFrequency(freq);
              setRoster((rows) => rows.map((r) => ({ ...r, billingFrequency: freq })));
            }}
            onBack={() => setStep(1)}
            onContinue={() => setStep(3)}
          />
        )}

        {step === 3 && (
          <ReviewBilling
            stepper={<WizardSteps current={2} steps={STEP_LABELS} />}
            roster={roster}
            frequency={frequency}
            rooms={activeRooms}
            isSaving={isSaving}
            onBack={() => setStep(2)}
            onConfirm={onConfirm}
          />
        )}
      </div>
    </div>
  );

  if (!chrome) return content;

  return (
    <MainContentLayout>
      <PageHeader pageName={t('Add families to billing')} classes="add-to-billing-page" />
      {content}
    </MainContentLayout>
  );
}

/** Routed page. */
export function AddToBilling() {
  return <AddToBillingFlow />;
}

export default AddToBilling;
